import type { MyChronGpsPoint, MyChronLapRecord, MyChronSessionExport } from './telemetrySession';
import { withGpsDerivedTelemetry } from './gpsDerivedTelemetry';
import { buildStartLineAtFraction, resliceLapsAtStartLine } from './trackEnrichment';

export function buildMyChronLapsFromGps(
  gps: MyChronGpsPoint[],
  options?: { minLapDurationMs?: number; maxLapDurationMs?: number },
): MyChronLapRecord[] {
  if (gps.length < 12) {
    return [];
  }

  const fullTracePolyline = gps.map((point) => ({ lat: point.lat, lon: point.lon }));
  if (fullTracePolyline.length < 12) {
    return [];
  }

  const session: MyChronSessionExport = {
    format: 'gpx',
    channels: [],
    laps: [],
    gps,
    series: {},
    metadata: {},
  };

  const startLine = buildStartLineAtFraction(fullTracePolyline, 0);
  const reslice = resliceLapsAtStartLine(session, startLine, {
    referencePolyline: fullTracePolyline,
    minLapDurationMs: options?.minLapDurationMs ?? 15_000,
    maxLapDurationMs: options?.maxLapDurationMs ?? 240_000,
  });

  return reslice.laps
    .filter((lap) => lap.kind === 'flying')
    .map((lap) => ({
      segment: 0,
      lapNumber: lap.lapNumber,
      durationMs: Math.round(lap.durationSec * 1000),
      endTimeMs: Math.round(lap.endMs),
    }));
}

export function buildMyChronSessionFromGps(
  gps: MyChronGpsPoint[],
  options: {
    sessionName: string;
    format?: string;
    startedAtIso?: string | null;
  },
): MyChronSessionExport {
  const laps = buildMyChronLapsFromGps(gps);
  const recordedAtMs = options.startedAtIso ? Date.parse(options.startedAtIso) : null;

  return withGpsDerivedTelemetry({
    format: options.format ?? 'gpx',
    channels: [],
    laps,
    gps,
    series: {},
    metadata: {
      venue: options.sessionName,
      ...(options.startedAtIso ? { startedAtIso: options.startedAtIso } : {}),
    },
    stats: {
      gpsSamples: gps.length,
      ...(recordedAtMs !== null && Number.isFinite(recordedAtMs) ? { recordedAtMs } : {}),
    },
  });
}
